<template>
<div>
  <va-modal
    id="contactDetailsModal"
    title=""
    size="medium"
    class="evollu-form"
    :value="showContactDetailsModal"
    :noOutsideDismiss="true"
    :okText="$t('modals.buttons.save')"
    :cancelText="$t('modals.buttons.cancel')"
    :dangerText="$t('modals.buttons.delete')"
    :showDelete="editingLead != null"
    @ok="saveLead"
    @cancel="resetFields"
    @remove="deleteLead">

    <div slot="header" class="modal-title">
      {{getModalTitle()}}
    </div>

    <div class="row" v-if="!editingLead && entitiesWrite.length > 1">
      <div class="col-12">
        <label class="form-label">{{$t('references.inputs.entity')}}</label>
        <div class="form-group">
          <div class="input-group">
            <multiselect
              v-model="form.entity"
              :options="entitiesWrite"
              track-by="id"
              label="name"
              :custom-label="nameWithCity"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('references.inputs.select_entity')"
              :allow-empty="false"
              @input="onEntitySelected"
              :multiple="false"
              :searchable="true"
              :disabled="!!editingLead"
              >
              <span slot="noResult">{{'patients.noResults'| translate}}</span>
            </multiselect>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="form.entity && facilitiesFromSelectedEntity.length > 1">
      <div class="col">
        <label class="form-label">{{$t('references.inputs.select_facility')}}</label>
        <div class="form-group">
          <div class="input-group">
            <multiselect
              v-model="form.facility"
              :options="facilitiesFromSelectedEntity"
              track-by="id"
              label="name"
              select-label=""
              :custom-label="nameWithCity"
              selected-label=""
              deselect-label=""
              :placeholder="$t('references.inputs.select_facility')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="!!editingLead"
              >
            </multiselect>
          </div>
        </div>
      </div>
    </div>

    <div class="newContactModal">
      <fieldset>
        <div class="row">
          <div class="col">
            <label class="form-label">{{$t('references.inputs.personal_info')}}</label>
            <div class="form-group">
              <div class="input-group">
                <va-input
                  :placeholder="$t('references.inputs.name')"
                  v-model="form.name"
                  id="editing-name"
                  required/>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  :placeholder="$t('references.inputs.surname')"
                  v-model="form.surname"
                  id="editing-surname"
                  required/>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="form-group with-icon-right">
              <div class="input-group">
                <va-input
                  id="successfulBirthdate"
                  name="successfulBirthdate"
                  v-model="form.birthdate"
                  :placeholder="$t('leads.details.birthdate')+' (DD/MM/YYYY)'"
                  :success="!invalidBirthdate && form.birthdate !== null && form.birthdate !== ''"
                  :error="invalidBirthdate"
                  :error-messages="[$t('references.errors.invalidBirthdate')]"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <div class="form-group" style="margin-bottom:0.25rem;">
              <div class="input-group">
                <vue-tel-input
                  :key="vueTelInputRedrawIndex"
                  v-model="form.phone"
                  inputId="phone-input-1"
                  class="w-100"
                  maxlength="20"
                  :preferredCountries="$store.state.countries.allowed"
                  :disabledFetchingCountry="true"
                  :defaultCountry="defaultCountrySelection"
                  :placeholder="$t('references.inputs.mobile_phone')"
                  @input="(value, options) => onEditingPhoneInput('phone', value, options)">
                </vue-tel-input>
              </div>
            </div>
            <div class="has-error form-group" style="margin-bottom:0rem;">
              <small v-show="invalidPhone" class="help text-danger pl-2" style="font-size: 0.8rem;">
                <i class="fa fa-exclamation-triangle error-icon icon-right"></i>
                {{'references.errors.invalidPhone' | translate}}
              </small>
            </div>
          </div>

          <div class="col-6">
            <div class="form-group" style="margin-bottom:0.25rem;">
              <div class="input-group">
                <vue-tel-input
                  :key="vueTelInputRedrawIndex"
                  v-model="form.phone_2"
                  inputId="phone-input-2"
                  class="w-100"
                  maxlength="20"
                  :preferredCountries="$store.state.countries.allowed"
                  :disabledFetchingCountry="true"
                  :defaultCountry="defaultCountrySelection"
                  :placeholder="$t('references.inputs.phone')"
                  @input="(value, options) => onEditingPhoneInput('phone_2', value, options)">
                </vue-tel-input>
              </div>
            </div>
            <div class="has-error form-group" style="margin-bottom:0rem;">
              <small v-show="invalidPhone2" class="help text-danger pl-2" style="font-size: 0.8rem;">
                <i class="fa fa-exclamation-triangle error-icon icon-right"></i>
                {{'references.errors.invalidPhone' | translate}}
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <div class="form-group with-icon-right" style="margin-top: 1rem; margin-bottom:1rem;">
              <div class="input-group">
                <va-input
                  id="successfulEmail"
                  name="successfulEmail"
                  v-model="form.email"
                  :placeholder="'Email ('+$t('references.inputs.optional')+')'"
                  :error="invalidEmail && form.email !== ''"
                  :error-messages="[$t('users.inputs.invalid_email')]"
                />
              </div>
            </div>
          </div>
        </div>
      </fieldset>

      <div class="row">
        <fieldset class="w-100">
          <div class="col">
            <label class="form-label">{{$t('leads.details.address')}}</label>
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="form.address_line_1"
                  :placeholder="$t('references.inputs.address_line_1')"/>
              </div>
            </div>
          </div>

          <div class="col">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="form.address_line_2"
                  :placeholder="$t('references.inputs.address_line_2')"/>
              </div>
            </div>
          </div>

          <div class="col">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="form.address_postal_code"
                  :placeholder="$t('references.inputs.address_postal_code')"/>
              </div>
            </div>
          </div>

          <div class="col">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="
                  form.address_city"
                  :placeholder="$t('references.inputs.address_city')"/>
              </div>
            </div>
          </div>

        </fieldset>
      </div>

      <div v-if="acusticaUser">
        <div class="col">
          <div class="form-group" v-if="acusticaUser">
            <div class="input-group">
              <va-input
                :label="$t('leads.details.health_system')"
                v-model="form.health_system"
                id="simple-input"
                required/>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <label class="form-label">{{'references.contactBestTime.title'| translate}}:</label>
          <div class="form-group">
            <div class="input-group">
              <multiselect
                v-model="form.appointment_option"
                :options="contactBestTime"
                track-by="id"
                label="value"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :searchable="false"
                >
              </multiselect>
            </div>
          </div>
        </div>
      </div>

      <div class="row" style="margin-bottom: 2rem !important;">
        <div class="col-12 col-md-6">
          <va-checkbox
            :label="$t('references.inputs.diagnosed_hl')"
            :id="'checkboxDiagnosedHL'"
            :isCircle="true"
            v-model="form.diagnosed_hl"/>
        </div>

        <div class="col-12 col-md-6">
          <va-checkbox
            :label="$t('references.inputs.hearing_aid')"
            :id="'checkboxHearingAid'"
            :isCircle="true"
            v-model="form.hearing_aid"/>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="form-group" v-if="!editingLead || (editingLead && form.obs) || !obsDisabled">
            <div class="input-group">
              <va-input
                :placeholder="$t('references.inputs.relevant_info')"
                type="textarea"
                :maxlength="250"
                v-model="form.obs"
                :disabled="obsDisabled"
                required/>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="form-group">
            <div class="input-group">
              <va-input
                :placeholder="$t('leads.details.comment')"
                type="textarea"
                :maxlength="250"
                v-model="form.pharma_comment"
                required/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </va-modal>

  <va-modal
    v-model="showDeleteContactModal"
    size="small"
    title=""
    :noOutsideDismiss="true"
    :okText="'references.inputs.delete' | translate"
    :cancelText="'modal.cancel' | translate"
    @cancel="closeDelete"
    @ok="confirmDelete"
    :okDisabled="!deletedReason"
    >
    <div slot="header">
      <p style="font-size:18px;"><b>{{$t('references.delete?')}}</b></p>
    </div>
    <div v-if="editingLead">
      <div style="margin-bottom:1.25rem;">{{'references.delete_message' | translate}} {{form.name}} {{form.surname}}?</div>
      <div class="form-group" style="margin-bottom:1.25rem;">
        <div class="input-group">
          <va-input
            type="textarea"
            v-model="deletedReason"
            id="delete-reason-input"
            :maxlength="250"
            required
            :label="$t('references.delete_reason')"/>
        </div>
      </div>
    </div>
  </va-modal>
</div>
</template>

<script>
import moment from 'moment'
import Multiselect from 'vue-multiselect'
import { VueTelInput } from 'vue-tel-input'
import { EMAIL_REGEX } from '@/utils/regex'

const toastOptions = {
  className: 'vuestic-toast',
  icon: 'fa-check',
  position: 'top-right',
  duration: 2500,
  fullWidth: false
}

const toastErrorOptions = {
  className: 'vuestic-toast danger',
  icon: 'fa-exclamation-triangle',
  position: 'top-right',
  duration: 2500,
  fullWidth: false
}

export default {
  props: ['editingLead', 'entitiesWrite', 'entities', 'facilitiesWrite', 'service'],
  components: {
    Multiselect,
    VueTelInput
  },
  data () {
    return {
      editingPhoneValid: true,
      editingPhone2Valid: true,
      showContactDetailsModal: false,
      showDeleteContactModal: false,
      vueTelInputRedrawIndex: 1,
      form: {
        name: '',
        surname: '',
        email: '',
        phone: '',
        phone_2: '',
        birthdate: '',
        address_line_1: '',
        address_line_2: '',
        address_postal_code: '',
        address_city: '',
        health_system: '',
        appointment_option: null,
        entity: null,
        diagnosed_hl: false,
        hearing_aid: false,
        facility: null,
        obs: '',
        pharma_comment: '',
        deleted_reason: '',
      },
      deletedReason: '',
      contactBestTime: [
        { id: 0, value: this.$t('references.contactBestTime.select_option') },
        { id: 1, value: this.$t('references.contactBestTime.early_morning') },
        { id: 2, value: this.$t('references.contactBestTime.late_morning') },
        { id: 3, value: this.$t('references.contactBestTime.early_afternoon') },
        { id: 4, value: this.$t('references.contactBestTime.late_afternoon') },
        { id: 5, value: this.$t('references.contactBestTime.no_contact') }
      ]
    }
  },
  mounted () {
    if (this.editingLead) {
      this.updateLead()
    } else {
      this.form.appointment_option = this.contactBestTime[0]
      if (this.entitiesWrite.length === 1) {
        this.form.entity = this.entitiesWrite[0]
      }
    }
    this.showContactDetailsModal = true
    this.showDeleteContactModal = false
  },
  computed: {
    isUpdating: function () {
      return this.editingLead !== null
    },
    obsDisabled: function () {
      return (this.editingLead && this.editingLead.kiosk)
    },
    facilitiesFromSelectedEntity: function () {
      if (!this.form.entity) {
        return null
      }
      return this.facilitiesWrite.filter(facility => facility.entity_id === this.form.entity.id)
    },
    authUserEntity: function () {
      return this.$auth.user().entity
    },
    defaultCountrySelection: function () {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country
      }
      return this.$store.state.countries.default
    },
    acusticaUser: function () {
      return this.authUserEntity.id === 8001 || (this.editingLead && this.form.entity.id === 8001)
    },
    invalidPhone: function () {
      if (this.form.phone) {
        return !this.editingPhoneValid
        // return !this.editingPhone.replace(/\s/g, '').match(/[9,2]\d{8,20}$/) || !this.editingPhoneValid
      }
      return false
    },
    invalidPhone2: function () {
      if (this.form.phone_2) {
        return !this.editingPhone2Valid
        // return !this.editingPhone2.replace(/\s/g, '').match(/[9,2]\d{8,20}$/)
      }
      return false
    },
    invalidEmail: function () {
      if (this.form.email) {
        return !this.form.email.match(EMAIL_REGEX)
      }
      return false
    },
    invalidBirthdate: function () {
      if (this.form.birthdate && this.form.birthdate !== '') {
        if (this.form.birthdate.match(/^[0-3]{1}\d{1}\/{1}[0-1]{1}\d{1}\/{1}\d{4}$/)) {
          let date = moment(this.form.birthdate, 'DD/MM/YYYY', true)
          return !(date.isValid() && date.isBefore())
        }
        return true
      }
      return false
    }
  },
  methods: {
    onEditingPhoneInput: function (field, value, options) {
      if (field === 'phone') {
        this.editingPhoneValid = options.valid
      } else {
        this.editingPhone2Valid = options.valid
      }
    },
    onEntitySelected: function (item) {
      if (item.facilities.length) {
        this.form.facility = item.facilities[0]
      }
    },
    saveLead: function () {
      let data = this.getFormData()

      let errors = Object.values(this.getFormErrors(data))

      // TODO: Refactor this to regular error's behaviour
      if (errors.length) {
        this.showToast(errors[0], toastErrorOptions)
        return
      }

      this.isUpdating ? this.update(data) : this.save(data)
    },
    getFormErrors: function (data) {
      let errors = {}

      if (!data.name || !data.phone) {
        errors['name_phone'] = this.$t('references.errors.no_name_phone')
      }

      if (this.invalidPhone || this.invalidPhone2) {
        errors['phone'] = this.$t('references.errors.invalidPhone')
      }

      if (this.invalidBirthdate) {
        errors['birthdate'] = this.$t('references.errors.invalidBirthdate')
      }

      if (data.appointment_option === null) {
        errors['appointment_option'] = this.$t('references.errors.no_contact_period')
      }

      return errors
    },
    save: function (data) {
      this.isloading = true
      this.$store.commit('SET_LOADING', true)
      this.$http({
        method: 'POST',
        url: this.service.url + '/leads',
        data
      }).then((response) => {
        this.$store.commit('SET_LOADING', false)
        this.isloading = false
        this.showToast(this.$t('references.errors.success'), toastOptions)
        this.$emit('close-contact-modal')
        this.$emit('update-table')
      }).catch((error) => {
        this.isloading = false
        this.showToast(this.$t('references.errors.server'), toastErrorOptions)
        console.log(error)
      })
    },
    update: function (data) {
      this.isloading = true
      this.$store.commit('SET_LOADING', true)
      this.$http({
        method: 'PATCH',
        url: `${this.service.url}/leads/${this.editingLead.id}`,
        data
      }).then((response) => {
        this.$store.commit('SET_LOADING', false)
        this.isloading = false
        this.showToast(this.$t('references.errors.success'), toastOptions)
        //this.$emit('close-contact-modal')
        this.$emit('update-table', response)
        this.showContactDetailsModal = false
      }).catch((error) => {
        this.isloading = false
        this.showToast(this.$t('references.errors.server'), toastErrorOptions)
        console.log(error)
      })
    },
    getFormData: function () {
      let form = JSON.parse(JSON.stringify(this.form))

      let body = Object.assign(form, {
        phone: this.form.phone ? this.form.phone.replace(/\s/g, '') : null,
        phone_2: this.form.phone_2 ? this.form.phone_2.replace(/\s/g, '') : null,
        email: this.form.email ? this.form.email.toLowerCase() : '',
        birthdate: this.form.birthdate ? moment(this.form.birthdate, 'DD/MM/YYYY', true).format('YYYY-MM-DD') : null,
        appointment_option: this.form.appointment_option ? this.form.appointment_option.id : null
      })

      if (!this.acusticaUser) {
        delete body.health_system
      }

      if (!this.isUpdating) {
        if (body.entity) {
          body.entity_id = body.entity.id
          delete body.entity
        }
        if (body.facility) {
          body.facility_id = body.facility.id
          delete body.facility
        }
        body.redirect_uri = window.location.href.split('#')[0]
      }
      return body
    },
    resetFields: function () {
      this.$emit('close-contact-modal')
    },
    nameWithCity ({ name, cities }) {
      if (cities == null || cities.length < 1) return name
      return name + ' (' + cities.join(', ') + ')'
    },
    deleteLead: function () {
      if (this.editingLead) {
        this.showDeleteContactModal = true
      }
    },
    closeDelete: function () {
      this.deletedReason = ''
      this.showDeleteContactModal = false
      this.showContactDetailsModal = true
    },
    confirmDelete: function () {
      this.$http({
        method: 'DELETE',
        url: this.service.url + '/leads/' + this.editingLead.id,
        data: { reason: this.deletedReason }
      }).then((response) => {
        this.deletedReason = ''
        this.showDeleteContactModal = false
        this.resetFields()
        this.$emit('update-table')
        this.showToast(this.$t('references.errors.delete_success'), toastOptions)
      }).catch((error) => {
        this.showToast(this.$t('references.errors.delete_error'), toastErrorOptions)
        this.deletedReason = ''
        this.showDeleteContactModal = false
        console.log(error)
      })
    },
    getModalTitle: function () {
      if (this.editingLead) {
        return `${this.$t('references.edit')} ${this.form.name}`
      }
      if (this.entitiesWrite.length > 1) {
        return this.$t('references.add')
      }
      return `${this.$t('references.add')} ${this.entitiesWrite[0].name}`
    },
    updateLead: function () {
      this.form = JSON.parse(JSON.stringify(this.editingLead))
      this.form.facility = this.facilitiesWrite.find(facility => facility.id === this.editingLead.facility_id)
      this.form.entity = this.getFullEntity(this.editingLead.entity_id)
      this.form.appointment_option = this.getFullPeriod(this.editingLead.appointment_option)
    },
    writableEntity: function (entityId) {
      this.entitiesWrite.some(entity => entity.id === entityId)
    },
    getFullEntity (entityId) {
      return this.entities.find(entity => entity.id === entityId)
    },
    getFullPeriod (option) {
      return this.contactBestTime.find(_option => _option.id === option)
    }
  },
  watch: {
    'form.entity': {
      handler: function (entity) {
        this.vueTelInputRedrawIndex++
      },
      deep: true
    },
    entitiesWrite: {
      handler: function () {
        if (this.writableEntity(this.authUserEntity)) {
          this.form.entity = this.authUserEntity
        } else {
          this.form.entity = this.entitiesWrite[0]
        }
        if (this.form.entity.facilities.length) {
          this.form.facility = this.form.entity.facilities[0]
        }
        if (this.editingLead && this.editingLead.appointment_option !== null) {
          this.form.appointment_option = this.contactBestTime.find(contactBestTime => contactBestTime.id === this.editingLead.appointment_option)
        }
      },
      immediate: true
    }
  },
}
</script>

<style lang="scss">

.newContactModal .form-group .control-label{
  z-index: 0;
}

.multiselect .multiselect__tag {
  background-color: #29c4a9;
}

.multiselect .multiselect__option--highlight {
  background-color: #29c4a9;
}

.multiselect .multiselect__option--highlight::after {
  background-color: #29c4a9;
}

.multiselect .multiselect__option--selected {
  background-color: #d9d9d9;
}

.multiselect .multiselect__option--selected.multiselect__option--highlight {
  background-color: #e34a4a;
}

.multiselect .multiselect__option--selected.multiselect__option--highlight::after {
  background-color: #e34a4a;
}

.multiselect__tag-icon:focus, .multiselect__tag-icon:hover {
  background: #21ab93 !important;
}

.contacts-checkboxes {
  margin-bottom: 2.5rem;
  .abc-checkbox {
    margin-bottom: 10px;
  }
}

</style>
<style lang="scss" scoped>
.evollu-form {
  .row {
    margin-bottom: 0 !important;
  }
}
</style>
